/**
 * SEO Utilities for ParkTrip Planner AI
 * Provides helpers for structured data, meta tags, and SEO optimization
 */

/**
 * Generate structured data for a trip/itinerary
 */
export const generateTripStructuredData = (tripData) => {
  if (!tripData) return null;

  return {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    "name": tripData?.destination || tripData?.title || "State Park Adventure",
    "description": tripData?.description || `Explore ${tripData?.park || 'beautiful state parks'}`,
    "touristType": "Outdoor Enthusiast",
    "itinerary": {
      "@type": "ItemList",
      "itemListElement": tripData?.itinerary?.map((day, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "TouristAttraction",
          "name": day?.title || day?.location || day?.parkName,
          "description": day?.activities?.join(', ') || day?.description
        }
      })) || []
    },
    "startDate": tripData?.startDate,
    "endDate": tripData?.endDate,
    "subjectOf": {
      "@type": "Trip",
      "name": tripData?.destination || "State Park Trip"
    }
  };
};

/**
 * Generate structured data for the organization
 */
export const generateOrganizationStructuredData = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "ParkTrip Planner AI",
    "url": "https://parktripplannerai.com",
    "logo": "https://images.unsplash.com/photo-1663646207502-ab871fc28489",
    "description": "AI-powered state park trip planning and itinerary generator",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Service",
      "email": "support@parktripplannerai.com"
    },
    "sameAs": [
    "https://facebook.com/parktripai",
    "https://twitter.com/parktripai",
    "https://instagram.com/parktripai"]

  };
};

/**
 * Generate structured data for a state park
 */
export const generateStateParkStructuredData = (parkData) => {
  if (!parkData) return null;

  return {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": parkData?.name,
    "description": parkData?.description,
    "image": parkData?.image,
    "address": {
      "@type": "PostalAddress",
      "addressRegion": parkData?.state,
      "addressCountry": "US"
    },
    "geo": parkData?.coordinates ? {
      "@type": "GeoCoordinates",
      "latitude": parkData?.coordinates?.lat,
      "longitude": parkData?.coordinates?.lng
    } : undefined,
    "tourBookingPage": `https://parktripplannerai.com/ai-trip-planning-interface?park=${parkData?.name}`
  };
};

/**
 * Generate breadcrumb structured data
 */
export const generateBreadcrumbStructuredData = (breadcrumbs) => {
  if (!breadcrumbs || breadcrumbs?.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs?.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb?.name,
      "item": `https://parktripplannerai.com${crumb?.path}`
    }))
  };
};

/**
 * Generate FAQ structured data
 */
export const generateFAQStructuredData = (faqs) => {
  if (!faqs || faqs?.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs?.map((faq) => ({
      "@type": "Question",
      "name": faq?.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq?.answer
      }
    }))
  };
};

/**
 * Default page titles for different sections
 */
export const PAGE_TITLES = {
  home: "ParkTrip Planner AI - AI-Powered State Park Trip Planning & Itinerary Generator",
  aiPlanner: "AI Trip Planner - Create Your Perfect State Park Adventure | ParkTrip AI",
  dashboard: "My Trips Dashboard - Manage Your Adventures | ParkTrip AI",
  login: "Sign In - Access Your Adventures | ParkTrip AI",
  register: "Create Account - Start Planning Adventures | ParkTrip AI",
  tripDetail: "Trip Details - View Your Itinerary | ParkTrip AI",
  tripConfirmation: "Confirm Your Trip - Save Your Adventure | ParkTrip AI",
  profile: "My Profile - Account Settings | ParkTrip AI",
  terms: "Terms of Service | ParkTrip AI",
  privacy: "Privacy Policy | ParkTrip AI"
};

/**
 * Default meta descriptions
 */
export const META_DESCRIPTIONS = {
  home: "Plan your perfect state park adventure with AI-powered personalized itineraries. Get expert recommendations for hiking, camping, and outdoor activities across America's beautiful state parks.",
  aiPlanner: "Let AI create your personalized state park itinerary. Answer a few questions and get a custom trip plan with activities, routes, and recommendations tailored to your preferences.",
  dashboard: "View and manage all your saved state park trips. Access your itineraries, upcoming adventures, and past trips in one convenient dashboard.",
  login: "Sign in to ParkTrip Planner AI to access your saved trips, create new itineraries, and discover personalized state park recommendations.",
  register: "Join ParkTrip Planner AI to save unlimited trip itineraries, get AI-powered recommendations, and plan unforgettable outdoor adventures.",
  tripDetail: "View your complete state park trip itinerary with day-by-day activities, maps, weather forecasts, and packing lists.",
  tripConfirmation: "Review and save your AI-generated state park itinerary. Customize your trip and access it anytime from your dashboard.",
  profile: "Manage your ParkTrip AI account settings, travel preferences, and notification options.",
  terms: "Read the Terms of Service for ParkTrip Planner AI including user agreements, service usage, and legal information.",
  privacy: "Learn how ParkTrip Planner AI protects your privacy and handles your personal data."
};

/**
 * Get Open Graph image for a page
 */
export const getOGImage = (pageType, customImage = null) => {
  if (customImage) return customImage;

  const baseUrl = "https://parktripplannerai.com";
  const images = {
    home: `${baseUrl}/assets/images/og-home.jpg`,
    trip: `${baseUrl}/assets/images/og-trip.jpg`,
    ai: `${baseUrl}/assets/images/og-ai-planner.jpg`,
    default: `${baseUrl}/assets/images/og-default.jpg`
  };

  return images?.[pageType] || images?.default;
};

/**
 * Format date for structured data
 */
export const formatStructuredDataDate = (date) => {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d?.getTime())) return null;
  return d?.toISOString()?.split('T')?.[0];
};

/**
 * Generate Twitter Card meta tags
 */
export const generateTwitterCardMeta = (title, description, image) => {
  return {
    "twitter:card": "summary_large_image",
    "twitter:site": "@parktripai",
    "twitter:title": title,
    "twitter:description": description,
    "twitter:image": image,
    "twitter:creator": "@parktripai"
  };
};

/**
 * Generate Open Graph meta tags
 */
export const generateOpenGraphMeta = (title, description, image, url) => {
  return {
    "og:type": "website",
    "og:url": url,
    "og:title": title,
    "og:description": description,
    "og:image": image,
    "og:image:width": "1200",
    "og:image:height": "630",
    "og:site_name": "ParkTrip Planner AI",
    "og:locale": "en_US"
  };
};

export default {
  generateTripStructuredData,
  generateOrganizationStructuredData,
  generateStateParkStructuredData,
  generateBreadcrumbStructuredData,
  generateFAQStructuredData,
  PAGE_TITLES,
  META_DESCRIPTIONS,
  getOGImage,
  formatStructuredDataDate,
  generateTwitterCardMeta,
  generateOpenGraphMeta
};